import React, { useState, useEffect } from "react";
import { Loader2, Users, Plus } from "lucide-react";
import DashboardContainer from "@/components/layout/DashboardContainer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { formatDateToBritish } from "@/lib/dateUtils";
import ContractorEntitiesList from "./ContractorEntitiesList";

interface ContractorSummary {
  id: number;
  email: string;
  status: 'active' | 'inactive' | 'suspended';
  lastActive: string;
  totalContracts: number;
  activeContracts: number;
  completedContracts: number;
}

interface ContractorsResponse {
  contractors: ContractorSummary[];
}

const OperationsContractorDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [loading, setLoading] = useState(true);
  const [contractors, setContractors] = useState<ContractorSummary[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newContractor, setNewContractor] = useState({ email: '', password: '' });
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchContractors = async () => {
      try {
        const response = await ApiService.request<ContractorsResponse>(
          EndpointKeys.OPERATIONS_CONTRACTORS
        );
        
        // Filter out demo contractors (emails containing "demo-contractor")
        const filteredContractors = response.contractors.filter(contractor => 
          !contractor.email.includes("demo-contractor")
        );
        
        setContractors(filteredContractors);
      } catch (error) {
        console.error("Error fetching contractors:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchContractors();
  }, []);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCreateContractor = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);

    try {
      const response = await ApiService.request<ContractorSummary>(
        EndpointKeys.OPERATIONS_CREATE_CONTRACTOR,
        {
          method: 'POST',
          body: newContractor
        }
      );

      setContractors(prev => [...prev, response]);
      setIsCreateDialogOpen(false);
      setNewContractor({ email: '', password: '' });
      
      toast({
        title: "Success",
        description: "Contractor created successfully",
      });
    } catch (error) {
      console.error("Error creating contractor:", error);
      toast({
        title: "Error",
        description: "Failed to create contractor",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <DashboardContainer
      title="Operations Contractor Dashboard"
      headerContent={
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-500">
            Monitor and manage contractor activities across the platform
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Contractor
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Contractor</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateContractor} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newContractor.email}
                    onChange={(e) => setNewContractor(prev => ({ ...prev, email: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={newContractor.password}
                    onChange={(e) => setNewContractor(prev => ({ ...prev, password: e.target.value }))}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isCreating}>
                  {isCreating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Contractor'
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      }
    >
      <Tabs
        defaultValue="overview"
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList className="mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="contractors">Contractors</TabsTrigger>
          <TabsTrigger value="entities">Entities</TabsTrigger>
          <TabsTrigger value="contracts">Contracts</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Card>
              <CardHeader>
                <div className="text-sm text-gray-500">Total Contractors</div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{contractors.length}</div>
                <div className="text-xs text-gray-500">Active platform users</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <div className="text-sm text-gray-500">Active Contracts</div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {contractors.reduce((sum, c) => sum + c.activeContracts, 0)}
                </div>
                <div className="text-xs text-gray-500">Currently active</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <div className="text-sm text-gray-500">Completed Contracts</div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {contractors.reduce((sum, c) => sum + c.completedContracts, 0)}
                </div>
                <div className="text-xs text-gray-500">Successfully completed</div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="contractors" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Contractor List</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Active</TableHead>
                    <TableHead>Total Contracts</TableHead>
                    <TableHead>Active Contracts</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contractors.map((contractor) => (
                    <TableRow key={contractor.id}>
                      <TableCell>{contractor.email}</TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(contractor.status)}>
                          {contractor.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDateToBritish(contractor.lastActive)}</TableCell>
                      <TableCell>{contractor.totalContracts}</TableCell>
                      <TableCell>{contractor.activeContracts}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="entities" className="space-y-6">
          <ContractorEntitiesList />
        </TabsContent>

        <TabsContent value="contracts" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Contract Management</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center text-gray-500">
                Contract management features coming soon
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </DashboardContainer>
  );
};

export default OperationsContractorDashboard; 